import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

import { filter, Observable, take } from 'rxjs';
import { Select } from '@ngxs/store';
import { ResearchState } from '../state/state';
import { environment } from '@env/environment';
import { Permission } from '@core/enums/permission';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Pipeline, Project } from 'app/modules/projects/projects.types';
import { Parcel, PropertyUseCode } from 'app/modules/research/research.types';
import { SaveParcelModalComponent } from 'app/modules/research/save-parcel-modal/save-parcel-modal.component';

@Component({
  selector: 'research-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResearchDetailsComponent implements OnInit {
  isLoading = false;
  parcel: any;
  pipelines: Pipeline[];
  propertyUseCodes = [];
  streetViewKey = environment.google.streetViewKey;
  Permission = Permission;

  @Select(ResearchState.selectedParcel) selectedParcel$: Observable<Parcel>;
  @Select(ResearchState.propertyUseCodes) propertyUseCodes$: Observable<PropertyUseCode[]>;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private _snackBar: MatSnackBar,
    private _matDialog: MatDialog,
    private _projectsService: ProjectsService,
    private _activatedRoute: ActivatedRoute,
    private _changeDetectorRef: ChangeDetectorRef,
    private _router: Router,
  ) {}

  ngOnInit(): void {
    this._activatedRoute.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: Data) => {
      this.propertyUseCodes = data.research_options.property_use_codes;
    });

    this._projectsService.pipelines$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((pipelines) => {
      if (pipelines) {
        this.pipelines = pipelines;
        this._changeDetectorRef.markForCheck();
      }
    });

    this.selectedParcel$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((parcel: any) => {
      this.parcel = this.transformParcelData(parcel);
    });
  }

  onGoogleMaps(address: string): void {
    window.open(`https://www.google.com/maps/place/${address}`);
  }

  onSave(parcel: Parcel): void {
    this._matDialog
      .open(SaveParcelModalComponent, {
        data: { parcel: parcel, pipelines: this.pipelines },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((newProject: Project) => !!newProject && !!newProject.id),
      )
      .subscribe((newProject: Project) => {
        this._snackBar
          .open('Deal has been saved.', 'Go to Deal', {
            duration: 3000,
          })
          .onAction()
          .subscribe(() => {
            this._router.navigate(['/projects', newProject.pipeline_id, newProject.id, 'details', 'overview']);
          });
      });
  }

  private transformParcelData(parcel: any): any {
    if (parcel.taxAssessor) {
      parcel.taxAssessor.owners = this.getOwners(parcel);
    } else {
      parcel.taxAssessor = { owners: [] };
    }

    parcel.recorders?.forEach((recorder: any) => {
      recorder.grantors = this.getGrantorsOrGrantees(recorder, 'grantor');
      recorder.grantees = this.getGrantorsOrGrantees(recorder, 'grantee');
      recorder.mortgages = this.getMortgages(recorder);
    });

    return parcel;
  }

  private getOwners(parcel: any): any[] {
    const owners = [] as any[];

    for (let i = 1; i <= 4; i++) {
      const ownerFullName = parcel.taxAssessor[`partyOwner${i}NameFull`];
      if (ownerFullName) {
        owners.push({ name: ownerFullName });
      }
    }

    return owners;
  }

  private getGrantorsOrGrantees(recorder: any, type: 'grantor' | 'grantee'): any[] {
    const items = [] as any[];

    for (let i = 1; i <= 4; i++) {
      const itemName = recorder[`${type}${i}NameFull`];
      if (itemName) {
        items.push({ name: itemName });
      }
    }

    return items;
  }

  private getMortgages(recorder: any): any[] {
    const mortgages = [] as any[];

    for (let i = 1; i <= 2; i++) {
      const lenderName = recorder[`mortgage${i}LenderNameFullStandardized`];
      const amount = recorder[`mortgage${i}Amount`];
      if (lenderName) {
        mortgages.push({ name: lenderName, amount });
      }
    }

    return mortgages;
  }
}
